import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Divider,
  FormControlLabel,
  FormGroup,
  Switch,
  Card,
  CardContent,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Stack,
  useTheme,
  alpha
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { BusinessContext } from '../../context/BusinessContext';
import { useBusinessSubscription } from '../../hooks/useBusinessSubscription';
import api from '../../utils/api';
import {
  supplierCategories,
  supplierCategoryLabels,
  supplierWeddingTypes,
  supplierVibeTags,
  supplierFormalityOptions,
  supplierStyleOptions,
  supplierCategoryGroups
} from '../../config/supplierTaxonomy';
import AddressMapSelector from './AddressMapSelector';
import PlaceAutocompleteInput from './PlaceAutocompleteInput';
import MyFilesSection from './MyFilesSection';
import { 
  Upload, 
  Trash, 
  Plus, 
  Check,
  MapPin,
  InstagramLogo,
  FacebookLogo,
  PinterestLogo,
  TiktokLogo,
  YoutubeLogo,
  Clock,
  Building,
  Image,
  Users,
  Star,
  FileText,
  ArrowSquareOut,
  Copy
} from '@phosphor-icons/react';

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, sectionId, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== sectionId}
      id={`profile-tabpanel-${sectionId}`}
      aria-labelledby={`profile-tab-${sectionId}`}
      {...other}
    >
      {value === sectionId && (
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Recommended Supplier Card Component
function RecommendedSupplierCard({ supplierId, onRemove, ownerBusinessId }) {
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchSupplier = async () => {
      try {
        setLoading(true);
        setError(false);
        // Try private endpoint first (works for recommended suppliers not publicly visible yet)
        try {
          const response = await api.get(`/api/supplier/recommended-suppliers/${supplierId}/details`, {
            params: ownerBusinessId ? { businessId: String(ownerBusinessId) } : undefined
          });
          const data = response.data;
          // Normalize data from supplier endpoint to match expected format
          const normalizedData = {
            ...data,
            name: data.name || data.businessName, // Handle both formats
            businessName: data.businessName || data.name, // Keep both for compatibility
            media: {
              ...(data.media || {}),
              logo: data.media?.logo || data.imageUrl || null
            },
            imageUrl: data.imageUrl || data.media?.logo || null
          };
          setSupplier(normalizedData);
        } catch {
          // If private endpoint fails, try public supplier endpoint
          try {
            const response = await api.get(`/api/supplier/${supplierId}`);
            const data = response.data;
            const normalizedData = {
              ...data,
              name: data.name || data.businessName,
              businessName: data.businessName || data.name,
              media: {
                ...(data.media || {}),
                logo: data.media?.logo || data.imageUrl || null
              },
              imageUrl: data.imageUrl || data.media?.logo || null
            };
            setSupplier(normalizedData);
          } catch {
            // If supplier endpoint fails, try public business endpoint
            try {
              const response = await api.get(`/api/public/business/${supplierId}`);
              setSupplier(response.data);
            } catch {
              // If both fail, mark as error but still show the card
              setError(true);
            }
          }
        }
      } catch (error) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (supplierId) {
      fetchSupplier();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [supplierId]);

  if (loading) {
    return (
      <Grid item xs={12} sm={6} md={4}>
        <Card>
          <CardContent>
            <CircularProgress size={24} />
          </CardContent>
        </Card>
      </Grid>
    );
  }

  // Show card even if supplier not found (with error state)
  if (error || !supplier) {
    return (
      <Grid item xs={12} sm={6} md={4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ width: 60, height: 60, bgcolor: 'error.light' }}>
                ?
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'error.main' }}>
                  Supplier Not Found
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {supplierId}
                </Typography>
              </Box>
            </Box>
            <Button
              size="small"
              variant="outlined"
              color="error"
              fullWidth
              startIcon={<Trash size={16} />}
              onClick={onRemove}
            >
              Remove
            </Button>
          </CardContent>
        </Card>
      </Grid>
    );
  }

  const categoryLabels = supplierCategoryLabels;

  // Normalize supplier data to handle different endpoint formats
  const supplierName = supplier.name || supplier.businessName || 'Unknown Supplier';
  const supplierLogo = supplier.media?.logo || supplier.imageUrl || null;

  return (
    <Grid item xs={12} sm={6} md={4}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              src={supplierLogo}
              sx={{ width: 60, height: 60 }}
            >
              {supplierName.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {supplierName}
              </Typography>
              <Chip
                label={categoryLabels[supplier.category] || supplier.category}
                size="small"
                color="primary"
              />
            </Box>
          </Box>
          <Button
            size="small"
            variant="outlined"
            color="error"
            fullWidth
            startIcon={<Trash size={16} />}
            onClick={onRemove}
          >
            Remove
          </Button>
        </CardContent>
      </Card>
    </Grid>
  );
}

const GALLERY_LIMIT_FREE = 5;
const GALLERY_LIMIT_PAID = 30;

/** YYYY-MM-DD in the browser's local calendar (for native date inputs and closed-date rules). */
function getLocalYmd(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function closedDateEntryLocalYmd(dateInput) {
  if (!dateInput) return '';
  const d = new Date(dateInput);
  if (Number.isNaN(d.getTime())) return '';
  return getLocalYmd(d);
}

function isClosedDateEntryInPast(entry) {
  const ymd = closedDateEntryLocalYmd(entry?.date);
  if (!ymd) return false;
  return ymd < getLocalYmd();
}

function filterClosedDatesNotInPast(closedDates) {
  if (!Array.isArray(closedDates)) return [];
  return closedDates.filter((e) => !isClosedDateEntryInPast(e));
}

const STYLE_PREFERENCES_MIN = 1;
const STYLE_PREFERENCES_MAX = 5;
const STYLE_PREFERENCE_LABELS = {
  weddingType: 'wedding types',
  vibeTags: 'style vibe tags'
};

const formatOptionLabel = (value) =>
  String(value || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

function SupplierProfileEdit() {
  const { loading: authLoading } = useAuth();
  const _navigate = useNavigate();
  const businessContext = useContext(BusinessContext);
  const { planKey } = useBusinessSubscription();
  const selectedBusiness = businessContext?.selectedBusiness;
  const selectedBusinessId = selectedBusiness?.business?._id;
  const refreshBusinesses = businessContext?.refreshBusinesses;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profile, setProfile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('basicInfo');
  const [newServiceArea, setNewServiceArea] = useState('');
  const [newServiceLocationInput, setNewServiceLocationInput] = useState('');
  const theme = useTheme();


  // Recommended Suppliers state
  const [recommendedSupplierDialogOpen, setRecommendedSupplierDialogOpen] = useState(false);
  const [recommendedSupplierId, setRecommendedSupplierId] = useState('');
  const [recommendedSuppliersLoading, setRecommendedSuppliersLoading] = useState(false);
  const [recommendedSupplierDialogError, setRecommendedSupplierDialogError] = useState('');

  // Categories for dropdown
  const categories = supplierCategories;

  // Helper functions

  const getCategoryIcon = (category) => {
    const iconMap = {
      venue: <Building size={24} />,
      photographer: <Image size={24} />,
      videographer: <Image size={24} />,
      content_creator: <Image size={24} />,
      caterer: <Star size={24} />,
      florist: <Star size={24} />,
      cake: <Star size={24} />,
      planner: <Users size={24} />,
      staffing: <Users size={24} />,
      celebrant_officiant: <Users size={24} />,
      security: <Users size={24} />,
      dj: <Star size={24} />,
      band: <Star size={24} />,
      entertainment: <Star size={24} />,
      hair: <Star size={24} />,
      makeup: <Star size={24} />,
      dress: <Star size={24} />,
      suit: <Star size={24} />,
      decor: <Star size={24} />,
      stationery: <Star size={24} />,
      furniture_hire: <Star size={24} />,
      lighting_av: <Star size={24} />,
      transport: <Star size={24} />,
      accommodation: <Star size={24} />,
      coordinator: <Users size={24} />,
      other: <Star size={24} />
    };
    return iconMap[category] || <Star size={24} />;
  };

  // Sections array for menu navigation
  const sections = profile ? [
    { id: 'basicInfo', label: 'Basic Information', icon: Building, description: 'Tell couples about your business' },
    { id: 'location', label: 'Location & Service Area', icon: MapPin, description: 'Where you work and serve couples' },
    { id: 'pricing', label: 'Pricing', icon: Star, description: 'Set your pricing information' },
    { id: 'images', label: 'Images & Gallery', icon: Image, description: 'Showcase your work with photos' },
    { id: 'contact', label: 'Contact & Social', icon: Users, description: 'How couples can reach you' },
    { id: 'stylePreferences', label: 'Style Preferences', icon: Star, description: 'Overall wedding style for matching with couples' },
    { id: 'categoryDetails', label: `${getCategoryLabel(profile.category)} Details`, icon: () => getCategoryIcon(profile.category), description: 'Add specific details about your services' },
    { id: 'policies', label: 'Policies & FAQ', icon: FileText, description: 'Set clear expectations' },
    { id: 'teamAndRecommendations', label: 'Recommended Suppliers', icon: Users, description: 'Recommend other suppliers and share your supplier ID' },
    { id: 'brochure', label: 'My Files', icon: FileText, description: 'Upload files such as menus, brochures, packages, and more' }
  ] : [];

  const fetchSupplierProfile = useCallback(async () => {
    try {
      setLoading(true);
      // Pass the selected business ID to get the correct business data
      const response = await api.get('/api/supplier/profile-edit', {
        params: selectedBusinessId ? { businessId: String(selectedBusinessId) } : {}
      });
      const data = response.data;
      setProfile({
        ...data,
        closedDates: filterClosedDatesNotInPast(data.closedDates)
      });
    } catch (error) {
      setError('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedBusinessId]);

  useEffect(() => {
    if (!authLoading && selectedBusinessId) {
      fetchSupplierProfile();
    }
  }, [authLoading, selectedBusinessId, fetchSupplierProfile]);


  const getSectionCompletion = (section) => {
    if (!profile) return 0;
    
    switch (section) {
      case 'basicInfo':
        const basicFields = ['businessName', 'category', 'description'];
        return basicFields.filter(field => {
          const value = profile[field];
          return value && (typeof value === 'string' ? value.trim() : value);
        }).length / basicFields.length * 100;
      
      case 'location':
        // Location is based on address
        return profile.address ? 100 : 0;
      
      case 'pricing':
        const pricingFields = ['startingPrice', 'pricingModel'];
        const pricingComplete = pricingFields.filter(field => {
          const value = profile[field];
          return value !== undefined && value !== null && (typeof value === 'string' ? value.trim() : true);
        }).length;
        return pricingComplete / pricingFields.length * 100;
      
      case 'images':
        const hasProfileImage = profile.imageUrl && (typeof profile.imageUrl === 'string' ? profile.imageUrl.trim() : profile.imageUrl);
        const hasGallery = profile.gallery && profile.gallery.length > 0;
        return ((hasProfileImage ? 1 : 0) + (hasGallery ? 1 : 0)) / 2 * 100;
      
      case 'contact':
        const contactFields = ['phone', 'email', 'address'];
        const contactComplete = contactFields.filter(field => {
          const value = profile[field];
          return value && (typeof value === 'string' ? value.trim() : value);
        }).length;
        return contactComplete / contactFields.length * 100;
      
      case 'categoryDetails':
        // Calculate completion based on category-specific fields
        if (!profile.category) return 0;
        
        // Venue
        if (profile.category === 'venue') {
          const venueFields = ['venueType', 'capacitySpecialization'];
          const completed = venueFields.filter(field => {
            const value = profile.venueDetails?.[field];
            return value && (typeof value === 'string' ? value.trim() : value);
          }).length;
          return (completed / venueFields.length) * 100;
        }
        
        // Photographer/Videographer
        if ((supplierCategoryGroups.media || []).includes(profile.category)) {
          const mediaFields = ['style'];
          const completed = mediaFields.filter(field => {
            const value = profile.mediaDetails?.[field];
            return value && (Array.isArray(value) ? value.length > 0 : value);
          }).length;
          return (completed / mediaFields.length) * 100;
        }
        
        // Decor
        if ((supplierCategoryGroups.decorLike || []).includes(profile.category)) {
          const decorFields = ['styleSpecialization', 'services'];
          const completed = decorFields.filter(field => {
            const value = profile.decorDetails?.[field];
            return value && (Array.isArray(value) ? value.length > 0 : value);
          }).length;
          return (completed / decorFields.length) * 100;
        }
        
        // Caterer
        if (profile.category === 'caterer') {
          const foodFields = ['serviceStyle'];
          const completed = foodFields.filter(field => {
            const value = profile.foodDetails?.[field];
            return value && (Array.isArray(value) ? value.length > 0 : value);
          }).length;
          return (completed / foodFields.length) * 100;
        }
        
        // Florist
        if (profile.category === 'florist') {
          const floristFields = ['styles', 'services'];
          const completed = floristFields.filter(field => {
            const value = profile.floristDetails?.[field];
            return value && (Array.isArray(value) ? value.length > 0 : value);
          }).length;
          return (completed / floristFields.length) * 100;
        }
        
        // Planner/Coordinator
        if ((supplierCategoryGroups.plannerLike || []).includes(profile.category)) {
          const plannerFields = ['services', 'specialization'];
          const completed = plannerFields.filter(field => {
            const value = profile.plannerDetails?.[field];
            return value && (Array.isArray(value) ? value.length > 0 : value);
          }).length;
          return (completed / plannerFields.length) * 100;
        }
        
        // Beauty (Hair/Makeup)
        if (profile.category === 'hair' || profile.category === 'makeup') {
          const beautyFields = ['styleSpecialization'];
          const completed = beautyFields.filter(field => {
            const value = profile.beautyDetails?.[field];
            return value && (Array.isArray(value) ? value.length > 0 : value);
          }).length;
          return (completed / beautyFields.length) * 100;
        }
        
        // Entertainment
        if ((supplierCategoryGroups.entertainmentLike || []).includes(profile.category)) {
          const entertainmentFields = ['performanceType'];
          if (['band', 'dj', 'music_live_entertainment'].includes(profile.category)) {
            entertainmentFields.push('genres');
          }
          const completed = entertainmentFields.filter(field => {
            const value = profile.entertainmentDetails?.[field];
            return value && (Array.isArray(value) ? value.length > 0 : value);
          }).length;
          return (completed / entertainmentFields.length) * 100;
        }
        
        // Cake, Dress, Suit - check for any details
        if (profile.category === 'cake' && profile.cakeDetails) {
          return Object.keys(profile.cakeDetails).length > 0 ? 50 : 0;
        }
        if ((profile.category === 'dress' || profile.category === 'suit' || profile.category === 'bridal_party_attire') && profile.fashionDetails) {
          return Object.keys(profile.fashionDetails).length > 0 ? 50 : 0;
        }
        if ((supplierCategoryGroups.transportLike || []).includes(profile.category) && profile.otherDetails) {
          return Object.keys(profile.otherDetails).length > 0 ? 50 : 0;
        }
        
        // Default for categories without specific details
        return 0;
      
      case 'policies':
        const policyFields = ['cancellation', 'deposit', 'payment'];
        return policyFields.filter(field => {
          const value = profile.policies?.[field];
          return value && (typeof value === 'string' ? value.trim() : value);
        }).length / policyFields.length * 100;
      
      case 'brochure':
        const files = profile.files || [];
        const hasFiles = files.length > 0;
        const hasPublicFiles = files.some(f => f.showOnProfile === true);
        return hasFiles ? (hasPublicFiles ? 100 : 50) : 0;
      
      case 'stylePreferences':
        // Style preferences completion - at least one weddingType or vibeTag
        const hasWeddingType = profile.weddingType && profile.weddingType.length > 0;
        const hasVibeTags = profile.vibeTags && profile.vibeTags.length > 0;
        const hasFormality = profile.formality && profile.formality.trim() !== '';
        const styleFields = [hasWeddingType, hasVibeTags, hasFormality].filter(Boolean).length;
        return (styleFields / 3) * 100;
      
      case 'teamAndRecommendations':
        // This section is optional - 100% if they have recommended suppliers
        const hasRecommendedSuppliers = profile.recommendedSuppliers && profile.recommendedSuppliers.length > 0;
        return hasRecommendedSuppliers ? 100 : 0;
      
      default:
        return 0;
    }
  };

  const getOverallCompletion = () => {
    const sections = ['basicInfo', 'location', 'pricing', 'images', 'contact', 'stylePreferences', 'categoryDetails', 'policies', 'teamAndRecommendations', 'brochure'];
    const totalCompletion = sections.reduce((sum, section) => sum + getSectionCompletion(section), 0);
    return totalCompletion / sections.length;
  };

  const handleChange = (field) => (event) => {
    setProfile({
      ...profile,
      [field]: event.target.value
    });
  };

  const handleOpeningHoursChange = (day, field, value) => {
    setProfile({
      ...profile,
      openingHours: {
        ...(profile.openingHours || {}),
        [day]: {
          ...(profile.openingHours?.[day] || {}),
          [field]: value
        }
      }
    });
  };

  const handleAddClosedDate = () => {
    const existing = Array.isArray(profile.closedDates) ? profile.closedDates : [];
    setProfile({
      ...profile,
      closedDates: [...existing, { date: '', reason: '' }]
    });
  };

  const handleClosedDateChange = (index, field, value) => {
    if (field === 'date' && value && value < getLocalYmd()) {
      return;
    }
    const existing = Array.isArray(profile.closedDates) ? [...profile.closedDates] : [];
    existing[index] = { ...(existing[index] || {}), [field]: value };
    setProfile({ ...profile, closedDates: existing });
  };

  const handleRemoveClosedDate = (index) => {
    const existing = Array.isArray(profile.closedDates) ? [...profile.closedDates] : [];
    existing.splice(index, 1);
    setProfile({ ...profile, closedDates: existing });
  };


  const handleSocialMediaChange = (platform) => (event) => {
    setProfile({
      ...profile,
      socialMedia: {
        ...profile.socialMedia,
        [platform]: event.target.value
      }
    });
  };

  const handleAddressChange = (value) => {
    setProfile({
      ...profile,
      address: value
    });
  };

  const handleAddressTypeChange = (addressType) => {
    setProfile({
      ...profile,
      addressType: addressType
    });
  };

  const _handleAddServiceArea = () => {
    if (newServiceArea.trim() && !(profile.serviceAreas || []).includes(newServiceArea.trim())) {
      setProfile({
        ...profile,
        serviceAreas: [...(profile.serviceAreas || []), newServiceArea.trim()]
      });
      setNewServiceArea('');
    }
  };

  const _handleRemoveServiceArea = (area) => {
    setProfile({
      ...profile,
      serviceAreas: (profile.serviceAreas || []).filter(a => a !== area)
    });
  };

  const handleAddServiceLocation = (place) => {
    if (!place?.address || place.latitude == null || place.longitude == null) return;
    const locations = profile.serviceLocations || [];
    const isPrimary = locations.length === 0;
    setProfile({
      ...profile,
      serviceLocations: [...locations, {
        address: place.address,
        latitude: place.latitude,
        longitude: place.longitude,
        isPrimary
      }]
    });
    setNewServiceLocationInput('');
  };

  const handleRemoveServiceLocation = (index) => {
    const locations = (profile.serviceLocations || []).filter((_, i) => i !== index);
    if (locations.length > 0 && !locations[0].isPrimary) {
      locations[0] = { ...locations[0], isPrimary: true };
    }
    setProfile({ ...profile, serviceLocations: locations });
  };

  const handlePricingChange = (field) => (event) => {
    const value = event.target.value;
    setProfile({
      ...profile,
      [field]: value
    });
  };

  const handleStylePreferenceToggle = (field, value) => {
    setError('');
    const currentValues = profile[field] || [];
    const isRemoving = currentValues.includes(value);

    if (!isRemoving && currentValues.length >= STYLE_PREFERENCES_MAX) {
      setError(`You can select up to ${STYLE_PREFERENCES_MAX} ${STYLE_PREFERENCE_LABELS[field]}.`);
      return;
    }

    const newValues = isRemoving
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];

    setProfile({
      ...profile,
      [field]: newValues
    });
  };


  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const weddingTypeCount = (profile.weddingType || []).length;
      const vibeTagsCount = (profile.vibeTags || []).length;

      if (weddingTypeCount < STYLE_PREFERENCES_MIN) {
        setError(`Please select at least ${STYLE_PREFERENCES_MIN} wedding type before saving.`);
        return;
      }
      if (vibeTagsCount < STYLE_PREFERENCES_MIN) {
        setError(`Please select at least ${STYLE_PREFERENCES_MIN} style vibe tag before saving.`);
        return;
      }
      if (weddingTypeCount > STYLE_PREFERENCES_MAX) {
        setError(`Please select no more than ${STYLE_PREFERENCES_MAX} wedding types.`);
        return;
      }
      if (vibeTagsCount > STYLE_PREFERENCES_MAX) {
        setError(`Please select no more than ${STYLE_PREFERENCES_MAX} style vibe tags.`);
        return;
      }

      // styleData is legacy read-shape from GET /profile-edit; do not send it back
      // because category detail edits are sourced from specific detail objects.
      const { styleData, ...profileWithoutStyleData } = profile;

      // Ensure all fields are included in the payload, even if empty
      const payload = {
        ...profileWithoutStyleData,
        // Explicitly include these fields to ensure they're sent
        policies: profile.policies || {},
        faqs: profile.faqs || [],
        files: profile.files || [],
        // Ensure pricing fields are included
        startingPrice: profile.startingPrice !== undefined && profile.startingPrice !== '' && profile.startingPrice !== null 
          ? parseFloat(profile.startingPrice) 
          : undefined,
        maxPrice: (() => {
          // Handle maxPrice explicitly - check if it exists and has a value
          if (profile.maxPrice === undefined || profile.maxPrice === null || profile.maxPrice === '') {
            return null; // Explicitly send null to clear the field
          }
          const numValue = typeof profile.maxPrice === 'string' ? parseFloat(profile.maxPrice) : Number(profile.maxPrice);
          return isNaN(numValue) ? null : numValue;
        })(),
        currency: 'GBP',
        pricingModel: profile.pricingModel,
        pricingNotes: profile.pricingNotes,
        // Ensure style preference fields are included
        weddingType: Array.isArray(profile.weddingType) ? profile.weddingType : [],
        vibeTags: Array.isArray(profile.vibeTags) ? profile.vibeTags : [],
        formality: profile.formality || null,
        closedDates: filterClosedDatesNotInPast(
          Array.isArray(profile.closedDates)
            ? profile.closedDates.filter(d => d && d.date)
            : []
        ),
      };

      const response = await api.put('/api/supplier/profile-edit', payload, {
        params: selectedBusinessId ? { businessId: String(selectedBusinessId) } : undefined
      });

      // Update the local profile state with the response from server
      const saved = response.data;
      setProfile({
        ...saved,
        closedDates: filterClosedDatesNotInPast(saved.closedDates)
      });

      setSuccess('Profile updated successfully!');

      // Refresh business context so header/menu shows updated business name immediately
      if (refreshBusinesses) {
        await refreshBusinesses();
      }

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type - only PNG and JPEG allowed
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError(`Only PNG and JPEG image files are allowed. Please select a PNG or JPEG file.`);
      // Reset the input so user can try again
      event.target.value = '';
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    try {
      setImageUploading(true);

      // Use different endpoints based on the image type
      let endpoint = '/api/supplier/upload-image'; // Default fallback

      if (type === 'imageUrl') {
        endpoint = '/api/supplier/upload-profile-image';
      } else if (type === 'coverImage') {
        endpoint = '/api/supplier/upload-cover-image';
      }

      const response = await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        params: selectedBusinessId ? { businessId: String(selectedBusinessId) } : undefined
      });

      // Update the appropriate image field
      const imageUrl = response.data.imageUrl || response.data.coverImage;

      setProfile({
        ...profile,
        [type]: imageUrl
      });

      setSuccess(`${type === 'imageUrl' ? 'Profile' : 'Cover'} image uploaded successfully`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '';
      if (errorMessage.includes('file type') || errorMessage.includes('PNG') || errorMessage.includes('JPEG')) {
        setError(errorMessage);
      } else {
        setError(`Failed to upload ${type === 'imageUrl' ? 'profile' : 'cover'} image. ${errorMessage || 'Please try again.'}`);
      }
      // Reset the input so user can try again
      event.target.value = '';
    } finally {
      setImageUploading(false);
    }
  };
  
  const handleGalleryUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const isFreeOrTrial = !planKey || planKey === 'supplier_free' || planKey === 'supplier_free_trial';
    const maxGallery = isFreeOrTrial ? GALLERY_LIMIT_FREE : GALLERY_LIMIT_PAID;
    const currentCount = profile.gallery?.length || 0;
    if (currentCount >= maxGallery) {
      setError(`Gallery limit reached (${maxGallery} photos${isFreeOrTrial ? ' on free plan' : ''}). Upgrade to add more.`);
      event.target.value = '';
      return;
    }

    // Validate file type - only PNG and JPEG allowed
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      setError('Only PNG and JPEG image files are allowed. Please select a PNG or JPEG file.');
      event.target.value = '';
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('image', file);

    try {
      setImageUploading(true);

      const response = await api.post('/api/supplier/upload-gallery-images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        params: selectedBusinessId ? { businessId: String(selectedBusinessId) } : undefined
      });

      // Update the gallery with the new image
      setProfile({
        ...profile,
        gallery: [...(profile.gallery || []), response.data.imageUrl]
      });

      setSuccess('Gallery image uploaded successfully');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || '';
      if (errorMessage.includes('file type') || errorMessage.includes('PNG') || errorMessage.includes('JPEG')) {
        setError(errorMessage);
      } else {
        setError(`Failed to upload gallery image. ${errorMessage || 'Please try again.'}`);
      }
      // Reset the input so user can try again
      event.target.value = '';
    } finally {
      setImageUploading(false);
    }
  };
  
  const handleDeleteGalleryImage = async (imageUrl, index) => {
    try {
      // First update the UI optimistically
      const newGallery = [...profile.gallery];
      newGallery.splice(index, 1);
      
      setProfile({
        ...profile,
        gallery: newGallery
      });
      
      // Then delete from the server
      await api.delete('/api/supplier/delete-gallery-image', {
        data: selectedBusinessId ? { imageUrl, businessId: String(selectedBusinessId) } : { imageUrl }
      });
      
      setSuccess('Gallery image deleted successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (error) {
      setError('Failed to delete gallery image. Please try again.');

      // Revert the optimistic update if the server request fails
      fetchSupplierProfile();
    }
  };

  if (loading || authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading profile...
        </Typography>
      </Box>
    );
  }

  if (!profile) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert 
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={fetchSupplierProfile}>
              Retry
            </Button>
          }
        >
          Failed to load supplier profile. Please try again.
        </Alert>
      </Box>
    );
  }

  const overallCompletion = getOverallCompletion();

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {/* Two Column Layout */}
      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        {/* Left Menu */}
        <Card sx={{ 
          width: { xs: '100%', md: 280 }, 
          height: 'fit-content', 
          position: { md: 'sticky' },
          top: { md: 20 },
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(10px)',
        }}>
          <CardContent sx={{ p: 0 }}>
            {/* Edit Profile Title */}
            <Box sx={{ px: 2, pt: 3, pb: 2 }}>
              <Typography variant="h5" component="h1" sx={{ fontWeight: 500 }}>
                Edit Profile
              </Typography>
            </Box>
            
            {/* Mobile Dropdown */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, px: 2, pb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Select Section</InputLabel>
                <Select
                  value={activeTab}
                  label="Select Section"
                  onChange={(e) => setActiveTab(e.target.value)}
                  renderValue={(value) => {
                    const section = sections.find(s => s.id === value);
                    if (!section) return value;
                    const completion = getSectionCompletion(section.id);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {section.label}
                        </Typography>
                        <Chip 
                          label={`${Math.round(completion)}%`}
                          size="small"
                          color={completion === 100 ? 'success' : completion >= 50 ? 'warning' : 'default'}
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    );
                  }}
                >
                  {sections.map((section) => {
                    const completion = getSectionCompletion(section.id);
                    return (
                      <MenuItem key={section.id} value={section.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                            <Box sx={{ color: activeTab === section.id ? 'primary.main' : 'text.secondary' }}>
                              {typeof section.icon === 'function' ? section.icon() : React.createElement(section.icon, { size: 20 })}
                            </Box>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontWeight: activeTab === section.id ? 500 : 400,
                                color: activeTab === section.id ? 'primary.main' : 'text.primary'
                              }}
                            >
                              {section.label}
                            </Typography>
                          </Box>
                          <Chip 
                            label={`${Math.round(completion)}%`}
                            size="small"
                            color={completion === 100 ? 'success' : completion >= 50 ? 'warning' : 'default'}
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>

            {/* Desktop List */}
            <List sx={{ p: 0, display: { xs: 'none', md: 'block' } }}>
              {sections.map((section) => {
                const completion = getSectionCompletion(section.id);
                const isActive = activeTab === section.id;
                return (
                  <ListItemButton
                    key={section.id}
                    onClick={() => setActiveTab(section.id)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      bgcolor: isActive ? '#ffffff' : 'transparent',
                      '&:hover': {
                        bgcolor: isActive ? '#ffffff' : alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Box sx={{ color: isActive ? 'primary.main' : 'text.secondary' }}>
                        {typeof section.icon === 'function' ? section.icon() : React.createElement(section.icon, { size: 24 })}
                      </Box>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: isActive ? 500 : 400,
                              color: isActive ? '#000000' : 'text.primary'
                            }}
                          >
                            {section.label}
                          </Typography>
                          <Chip 
                            label={`${Math.round(completion)}%`}
                            size="small"
                            color={completion === 100 ? 'success' : completion >= 50 ? 'warning' : 'default'}
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </CardContent>
          
          {/* Completion Bar Card */}
          <Card sx={{ 
            m: 2, 
            background: 'linear-gradient(135deg, rgba(248, 165, 194, 0.1) 0%, rgba(252, 228, 236, 0.1) 100%)',
            border: '1px solid rgba(248, 165, 194, 0.2)'
          }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Profile Completion
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={overallCompletion}
                sx={{ 
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(248, 165, 194, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: 3,
                  },
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {Math.round(overallCompletion)}% Complete
              </Typography>
            </CardContent>
          </Card>

        </Card>

        {/* Right Content Area */}
        <Box sx={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}>
          {/* Tab Content */}
          <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible' }}>
            <CardContent sx={{ p: 0 }}>
              {/* Basic Information Tab */}
              <TabPanel value={activeTab} sectionId="basicInfo">
                <Box sx={{ 
                  mb: { xs: 1.5, sm: 2, md: 3 }, 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', sm: 'flex-start' },
                  gap: { xs: 1.5, sm: 2, md: 0 }
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="h5" 
                      component="h2" 
                      sx={{ 
                        mb: 1,
                        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                      }}
                    >
                      {sections.find(s => s.id === 'basicInfo')?.label || 'Basic Information'}
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1rem' } }}
                    >
                      {sections.find(s => s.id === 'basicInfo')?.description || 'Tell couples about your business'}
                    </Typography>
                  </Box>
                  {profile.slug && selectedBusinessId && (
                    <Button
                      variant="outlined"
                      startIcon={<ArrowSquareOut size={20} />}
                      onClick={() => {
                        const profileUrl = `/profile/${selectedBusinessId}/${profile.slug}`;
                        window.open(profileUrl, '_blank', 'noopener,noreferrer');
                      }}
                      sx={{ 
                        ml: { xs: 0, sm: 2 },
                        mt: { xs: 1, sm: 0 },
                        width: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      View Profile Page
                    </Button>
                  )}
                </Box>
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Business Name"
                    value={profile.businessName || ''}
                    onChange={handleChange('businessName')}
                    required
                    helperText="This is how couples will find you"
                  />
                </Grid>
                {profile.slug && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Profile URL Slug"
                      value={profile.slug || ''}
                      disabled
                      helperText={profile.slug ? `Your public profile: /profile/${selectedBusinessId || 'ID'}/${profile.slug}` : 'Slug will be generated automatically when you save'}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required disabled>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={profile.category || ''}
                      onChange={handleChange('category')}
                      label="Category"
                      disabled
                    >
                      {categories.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    value={profile.description || ''}
                    onChange={handleChange('description')}
                    multiline
                    rows={4}
                    helperText="Describe your business, services, and what makes you unique"
                  />
                </Grid>
                
                {/* Opening Hours Section */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Clock size={20} />
                    <Typography variant="h6">Opening Hours</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Set your business hours to control when couples can book
                  </Typography>
                  
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                      <Grid item xs={12} sm={6} md={3} key={day}>
                        <Card variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'row', sm: 'column' },
                            alignItems: { xs: 'center', sm: 'center' },
                            justifyContent: { xs: 'space-between', sm: 'center' },
                            gap: { xs: 1, sm: 1 }
                          }}>
                            <Typography 
                              variant="subtitle1" 
                              sx={{ 
                                textTransform: 'capitalize', 
                                fontWeight: 500,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                              }}
                            >
                              {day}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={profile.openingHours?.[day]?.isOpen || false}
                                  onChange={(e) => handleOpeningHoursChange(day, 'isOpen', e.target.checked)}
                                  size="small"
                                />
                              }
                              label=""
                              sx={{ m: 0 }}
                            />
                            {!profile.openingHours?.[day]?.isOpen && (
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  display: { xs: 'none', sm: 'block' },
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                                }}
                              >
                                Closed
                              </Typography>
                            )}
                          </Box>
                          {!profile.openingHours?.[day]?.isOpen && (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ 
                                display: { xs: 'block', sm: 'none' },
                                textAlign: 'center',
                                mt: { xs: 0.5, sm: 0 },
                                fontSize: { xs: '0.75rem', sm: '0.875rem' }
                              }}
                            >
                              Closed
                            </Typography>
                          )}
                          
                          {profile.openingHours?.[day]?.isOpen && (
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: { xs: 'row', sm: 'column' },
                              gap: { xs: 1, sm: 1 }, 
                              alignItems: { xs: 'center', sm: 'center' },
                              justifyContent: { xs: 'center', sm: 'center' },
                              mt: { xs: 1, sm: 1 },
                              width: '100%'
                            }}>
                              <TextField
                                size="small"
                                type="time"
                                value={profile.openingHours?.[day]?.openTime || '09:00'}
                                onChange={(e) => handleOpeningHoursChange(day, 'openTime', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ 
                                  flex: { xs: '1 1 0', sm: 1 },
                                  width: { xs: 'auto', sm: '100%' },
                                  minWidth: 0
                                }}
                              />
                              <Typography 
                                variant="body2"
                                sx={{ 
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  flexShrink: 0
                                }}
                              >
                                to
                              </Typography>
                              <TextField
                                size="small"
                                type="time"
                                value={profile.openingHours?.[day]?.closeTime || '17:00'}
                                onChange={(e) => handleOpeningHoursChange(day, 'closeTime', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                sx={{ 
                                  flex: { xs: '1 1 0', sm: 1 },
                                  width: { xs: 'auto', sm: '100%' },
                                  minWidth: 0
                                }}
                              />
                            </Box>
                          )}
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>

                {/* Closed Dates Section */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 2 }}>
                    <Clock size={20} />
                    <Typography variant="h6">Closed Dates</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Add specific dates you&apos;re closed (public holidays, annual leave, events). Couples won&apos;t be able to request appointments on these days.
                  </Typography>

                  <Stack spacing={1.5} sx={{ mb: 2 }}>
                    {(profile.closedDates || []).map((entry, index) => {
                      const dateValue = closedDateEntryLocalYmd(entry?.date);
                      return (
                        <Box
                          key={`closed-date-${index}`}
                          sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 1,
                            alignItems: { xs: 'stretch', sm: 'center' }
                          }}
                        >
                          <TextField
                            size="small"
                            type="date"
                            label="Date"
                            value={dateValue}
                            onChange={(e) => handleClosedDateChange(index, 'date', e.target.value)}
                            inputProps={{ min: getLocalYmd() }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 180 }}
                          />
                          <TextField
                            size="small"
                            label="Reason (optional)"
                            value={entry.reason || ''}
                            onChange={(e) => handleClosedDateChange(index, 'reason', e.target.value)}
                            placeholder="e.g. Bank holiday, Annual leave"
                            sx={{ flex: 1 }}
                          />
                          <IconButton
                            aria-label="Remove closed date"
                            onClick={() => handleRemoveClosedDate(index)}
                            size="small"
                          >
                            <Trash size={18} />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Stack>

                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    startIcon={<Plus size={16} />}
                    onClick={handleAddClosedDate}
                  >
                    Add closed date
                  </Button>
                </Grid>
              </Grid>
              </TabPanel>

              {/* Location & Service Area Tab */}
              <TabPanel value={activeTab} sectionId="location">
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    {sections.find(s => s.id === 'location')?.label || 'Location & Service Area'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sections.find(s => s.id === 'location')?.description || 'Where you work and serve couples'}
                  </Typography>
                </Box>
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
                  <Grid item xs={12}>
                    <AddressMapSelector
                      label="Business Address"
                      placeholder="Enter your business address..."
                      value={profile.address || ''}
                      onChange={handleAddressChange}
                      addressType={profile.addressType || 'business'}
                      onAddressTypeChange={handleAddressTypeChange}
                      helperText="Select business or private address. Private addresses are hidden on the map for privacy."
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Service Locations
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      The first location is your primary (main base); additional locations are secondary areas you serve. We use coordinates for supplier search.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <PlaceAutocompleteInput
                          value={newServiceLocationInput}
                          onChange={setNewServiceLocationInput}
                          onSelect={(place) => {
                            handleAddServiceLocation(place);
                            setNewServiceLocationInput('');
                          }}
                          placeholder="Search for city, town or address..."
                          label=""
                        />
                      </Box>
                      <Button
                        type="button"
                        variant="outlined"
                        disabled={!newServiceLocationInput.trim()}
                        sx={{ mt: 0.5 }}
                        onClick={async () => {
                          if (!newServiceLocationInput.trim()) return;
                          try {
                            const res = await api.post('/api/geocode/address', { address: newServiceLocationInput.trim() });
                            if (res.data?.success && res.data?.data?.coordinates) {
                              const [lng, lat] = res.data.data.coordinates;
                              handleAddServiceLocation({
                                address: res.data.data.formattedAddress || newServiceLocationInput,
                                latitude: lat,
                                longitude: lng
                              });
                              setNewServiceLocationInput('');
                            }
                          } catch { /* ignore */ }
                        }}
                      >
                        Add
                      </Button>
                    </Box>
                    {(profile.serviceLocations || []).length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {profile.serviceLocations.map((loc, index) => (
                          <Chip
                            key={`${loc.address}-${index}`}
                            label={`${loc.isPrimary ? 'Primary' : 'Secondary'}: ${loc.address}`}
                            onDelete={() => handleRemoveServiceLocation(index)}
                            color={loc.isPrimary ? 'primary' : 'default'}
                            variant={loc.isPrimary ? 'filled' : 'outlined'}
                            sx={{ maxWidth: '100%' }}
                          />
                        ))}
                      </Box>
                    )}
                  </Grid>
                  
                </Grid>
              </TabPanel>

              {/* Pricing Tab */}
              <TabPanel value={activeTab} sectionId="pricing">
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    {sections.find(s => s.id === 'pricing')?.label || 'Pricing'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sections.find(s => s.id === 'pricing')?.description || 'Set your pricing information'}
                  </Typography>
                </Box>
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Starting Price"
                      type="number"
                      value={profile.startingPrice || ''}
                      onChange={handlePricingChange('startingPrice')}
                      variant="outlined"
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Your starting price for services"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Max Price (Optional)"
                      type="number"
                      value={profile.maxPrice || ''}
                      onChange={handlePricingChange('maxPrice')}
                      variant="outlined"
                      inputProps={{ min: 0, step: 0.01 }}
                      helperText="Maximum price (must be ≥ starting price)"
                      error={profile.maxPrice && profile.startingPrice && parseFloat(profile.maxPrice) < parseFloat(profile.startingPrice)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Pricing Model</InputLabel>
                      <Select
                        value={profile.pricingModel || 'flat_fee'}
                        onChange={handlePricingChange('pricingModel')}
                        label="Pricing Model"
                      >
                        <MenuItem value="per_person">Per Person</MenuItem>
                        <MenuItem value="flat_fee">Flat Fee</MenuItem>
                        <MenuItem value="per_hour">Per Hour</MenuItem>
                        <MenuItem value="custom">Custom</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Pricing Notes (Optional)"
                      value={profile.pricingNotes || ''}
                      onChange={handlePricingChange('pricingNotes')}
                      variant="outlined"
                      multiline
                      rows={3}
                      placeholder="e.g., Starting from £5,000 for full day coverage"
                      helperText="Add any additional pricing information or notes"
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Images & Gallery Tab */}
              <TabPanel value={activeTab} sectionId="images">
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    {sections.find(s => s.id === 'images')?.label || 'Images & Gallery'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sections.find(s => s.id === 'images')?.description || 'Showcase your work with photos'}
                  </Typography>
                </Box>
              <Typography variant="h6" gutterBottom>
                Profile Images
              </Typography>
              <Grid container spacing={3}>
                {/* Profile Image Upload */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Profile Image
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 200,
                        border: '1px dashed #ccc',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 1,
                        backgroundImage: profile.imageUrl ? `url(${profile.imageUrl})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative'
                      }}
                    >
                      {!profile.imageUrl && (
                        <Upload size={40} weight="thin" color="#999" />
                      )}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={(e) => handleImageUpload(e, 'imageUrl')}
                        style={{
                          opacity: 0,
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer'
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                      Click to upload (max 5MB)
                    </Typography>
                    <Typography variant="caption" color="error" sx={{ textAlign: 'center', display: 'block', mt: 0.5 }}>
                      Only PNG and JPEG files are supported
                    </Typography>
                  </Box>
                </Grid>

                {/* Cover Image Upload */}
                <Grid item xs={12} md={8}>
                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Cover Image
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 200,
                        border: '1px dashed #ccc',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 1,
                        backgroundImage: profile.coverImage ? `url(${profile.coverImage})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        position: 'relative'
                      }}
                    >
                      {!profile.coverImage && (
                        <Upload size={40} weight="thin" color="#999" />
                      )}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={(e) => handleImageUpload(e, 'coverImage')}
                        style={{
                          opacity: 0,
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          cursor: 'pointer'
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
                      Click to upload (max 5MB)
                    </Typography>
                    <Typography variant="caption" color="error" sx={{ textAlign: 'center', display: 'block', mt: 0.5 }}>
                      Only PNG and JPEG files are supported
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Gallery
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Add photos to showcase your work. These will be displayed in your public profile.
              </Typography>
              {(() => {
                const isFreeOrTrial = !planKey || planKey === 'supplier_free' || planKey === 'supplier_free_trial';
                const maxGallery = isFreeOrTrial ? GALLERY_LIMIT_FREE : GALLERY_LIMIT_PAID;
                const currentCount = profile.gallery?.length || 0;
                return (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {currentCount} / {maxGallery} photos{isFreeOrTrial && ' (upgrade for up to 30)'}
                  </Typography>
                );
              })()}

              <Box sx={{ mt: 3 }}>
                <Grid container spacing={2}>
                  {profile.gallery?.map((image, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index}>
                      <Box
                        sx={{
                          position: 'relative',
                          height: 150,
                          overflow: 'hidden',
                          backgroundImage: `url(${image})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          '&:hover .overlay': {
                            opacity: 1
                          }
                        }}
                      >
                        <Box
                          className="overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            opacity: 0,
                            transition: 'opacity 0.2s'
                          }}
                        >
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteGalleryImage(image, index)}
                          >
                            <Trash size={24} />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  ))}

                  {/* Add new image */}
                  {(() => {
                    const isFreeOrTrial = !planKey || planKey === 'supplier_free' || planKey === 'supplier_free_trial';
                    const maxGallery = isFreeOrTrial ? GALLERY_LIMIT_FREE : GALLERY_LIMIT_PAID;
                    const currentCount = profile.gallery?.length || 0;
                    const atLimit = currentCount >= maxGallery;
                    return (
                      <Grid item xs={6} sm={4} md={3}>
                        <Box
                          sx={{
                            height: 150,
                            border: '1px dashed #ccc',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: atLimit ? 'not-allowed' : 'pointer',
                            position: 'relative',
                            opacity: atLimit ? 0.5 : 1
                          }}
                        >
                          <Plus size={32} weight="thin" color="#999" />
                          {!atLimit && (
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/jpg"
                              onChange={handleGalleryUpload}
                              style={{
                                opacity: 0,
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%'
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" align="center" display="block" sx={{ mt: 1 }}>
                          {atLimit ? 'Limit reached' : 'Add Image'}
                        </Typography>
                      </Grid>
                    );
                  })()}
                </Grid>
                
                {imageUploading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      Uploading image...
                    </Typography>
                  </Box>
                )}
              </Box>
              </TabPanel>

              {/* Contact & Social Tab */}
              <TabPanel value={activeTab} sectionId="contact">
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    {sections.find(s => s.id === 'contact')?.label || 'Contact & Social'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sections.find(s => s.id === 'contact')?.description || 'How couples can reach you'}
                  </Typography>
                </Box>
              <Typography variant="h6" gutterBottom>
                Contact Information
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={profile.phone || ''}
                    onChange={handleChange('phone')}
                    helperText="Your business phone number"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={profile.email || ''}
                    onChange={handleChange('email')}
                    type="email"
                    helperText="Your business email address"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Website"
                    value={profile.website || ''}
                    onChange={handleChange('website')}
                    helperText="Your business website (optional)"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Social Media
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Instagram"
                    value={profile.socialMedia?.instagram || ''}
                    onChange={handleSocialMediaChange('instagram')}
                    InputProps={{
                      startAdornment: <InstagramLogo size={20} style={{ marginRight: 8 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Facebook"
                    value={profile.socialMedia?.facebook || ''}
                    onChange={handleSocialMediaChange('facebook')}
                    InputProps={{
                      startAdornment: <FacebookLogo size={20} style={{ marginRight: 8 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Pinterest"
                    value={profile.socialMedia?.pinterest || ''}
                    onChange={handleSocialMediaChange('pinterest')}
                    InputProps={{
                      startAdornment: <PinterestLogo size={20} style={{ marginRight: 8 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="TikTok"
                    value={profile.socialMedia?.tiktok || ''}
                    onChange={handleSocialMediaChange('tiktok')}
                    InputProps={{
                      startAdornment: <TiktokLogo size={20} style={{ marginRight: 8 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="YouTube"
                    value={profile.socialMedia?.youtube || ''}
                    onChange={handleSocialMediaChange('youtube')}
                    InputProps={{
                      startAdornment: <YoutubeLogo size={20} style={{ marginRight: 8 }} />
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="X"
                    value={profile.socialMedia?.twitter || ''}
                    onChange={handleSocialMediaChange('twitter')}
                    InputProps={{
                      startAdornment: (
                        <Box
                          component="svg"
                          viewBox="0 0 24 24"
                          width={20}
                          height={20}
                          sx={{
                            marginRight: 1,
                            fill: 'currentColor',
                            display: 'inline-block'
                          }}
                        >
                          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                        </Box>
                      )
                    }}
                  />
                </Grid>
              </Grid>
              </TabPanel>

              {/* Style Preferences Tab */}
              <TabPanel value={activeTab} sectionId="stylePreferences">
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    Style Preferences
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Set your overall wedding style preferences to help us match you with couples who share your vision.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Select between 1 and 5 wedding types, and 1 and 5 style vibe tags. We recommend 3 to 5 in each.
                  </Typography>
                </Box>

                <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
                  {/* Wedding Types */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Wedding Types You Specialise In
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {supplierWeddingTypes.map((type) => (
                        <Chip
                          key={type}
                          label={formatOptionLabel(type)}
                          onClick={() => handleStylePreferenceToggle('weddingType', type)}
                          color={(profile.weddingType || []).includes(type) ? 'primary' : 'default'}
                          variant={(profile.weddingType || []).includes(type) ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Vibe Tags */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Style Vibe Tags
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {supplierVibeTags.map((tag) => (
                        <Chip
                          key={tag}
                          label={formatOptionLabel(tag)}
                          onClick={() => handleStylePreferenceToggle('vibeTags', tag)}
                          color={(profile.vibeTags || []).includes(tag) ? 'primary' : 'default'}
                          variant={(profile.vibeTags || []).includes(tag) ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Box>
                  </Grid>

                  {/* Formality */}
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Formality Level</InputLabel>
                      <Select
                        value={profile.formality || ''}
                        onChange={(e) => setProfile({ ...profile, formality: e.target.value })}
                        label="Formality Level"
                      >
                        <MenuItem value="">Select...</MenuItem>
                        {supplierFormalityOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Category Details Tab */}
              <TabPanel value={activeTab} sectionId="categoryDetails">
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    {sections.find(s => s.id === 'categoryDetails')?.label || 'Category Details'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sections.find(s => s.id === 'categoryDetails')?.description || 'Add specific details about your services'}
                  </Typography>
                </Box>
              {/* Render different form fields based on category */}
              {profile.category === 'venue' && (
                <VenueFields profile={profile} setProfile={setProfile} />
              )}
              
              {profile.category === 'photographer' && (
                <PhotographerFields profile={profile} setProfile={setProfile} />
              )}
              
              {((supplierCategoryGroups.media || []).includes(profile.category) && profile.category !== 'photographer') && (
                <VideographerFields profile={profile} setProfile={setProfile} />
              )}
              
              {(supplierCategoryGroups.decorLike || []).includes(profile.category) && (
                <DecorFields profile={profile} setProfile={setProfile} />
              )}
              
              {profile.category === 'caterer' && (
                <CatererFields profile={profile} setProfile={setProfile} />
              )}
              
              {profile.category === 'florist' && (
                <FloristFields profile={profile} setProfile={setProfile} />
              )}
              
              {profile.category === 'cake' && (
                <CakeMakerFields profile={profile} setProfile={setProfile} />
              )}
              
              {(profile.category === 'dress' || profile.category === 'suit' || profile.category === 'bridal_party_attire') && (
                <DressDesignerFields profile={profile} setProfile={setProfile} />
              )}
              
              {(supplierCategoryGroups.plannerLike || []).includes(profile.category) && profile.category !== 'coordinator' && (
                <PlannerFields profile={profile} setProfile={setProfile} />
              )}
              
              {profile.category === 'coordinator' && (
                <PlannerFields profile={profile} setProfile={setProfile} />
              )}

              {(supplierCategoryGroups.transportLike || []).includes(profile.category) && (
                <OtherFields profile={profile} setProfile={setProfile} />
              )}
              
              {(supplierCategoryGroups.entertainmentLike || []).includes(profile.category) && (
                <EntertainmentFields profile={profile} setProfile={setProfile} />
              )}
              
              {(profile.category === 'hair' || profile.category === 'makeup') && (
                <BeautyFields profile={profile} setProfile={setProfile} />
              )}
              
              </TabPanel>

              {/* Policies & FAQ Tab */}
              <TabPanel value={activeTab} sectionId="policies">
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    {sections.find(s => s.id === 'policies')?.label || 'Policies & FAQ'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sections.find(s => s.id === 'policies')?.description || 'Set clear expectations'}
                  </Typography>
                </Box>
              <Typography variant="h6" gutterBottom>
                Policies
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Cancellation Policy"
                    value={profile.policies?.cancellation || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      policies: {
                        ...profile.policies,
                        cancellation: e.target.value
                      }
                    })}
                    multiline
                    rows={3}
                    helperText="When and how couples can cancel"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Deposit Policy"
                    value={profile.policies?.deposit || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      policies: {
                        ...profile.policies,
                        deposit: e.target.value
                      }
                    })}
                    multiline
                    rows={3}
                    helperText="Deposit requirements and terms"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Payment Policy"
                    value={profile.policies?.payment || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      policies: {
                        ...profile.policies,
                        payment: e.target.value
                      }
                    })}
                    multiline
                    rows={3}
                    helperText="Payment terms and methods"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Refund Policy"
                    value={profile.policies?.refund || ''}
                    onChange={(e) => setProfile({
                      ...profile,
                      policies: {
                        ...profile.policies,
                        refund: e.target.value
                      }
                    })}
                    multiline
                    rows={3}
                    helperText="Refund terms and conditions"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Frequently Asked Questions
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Add common questions and answers so couples understand your services.
              </Typography>

              <Box sx={{ mt: 3 }}>
                {profile.faqs?.map((faq, index) => (
                  <Paper key={index} sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                      <Grid item xs={12} md={5}>
                        <TextField
                          fullWidth
                          label="Question"
                          value={faq.question}
                          onChange={(e) => {
                            const newFaqs = [...profile.faqs];
                            newFaqs[index].question = e.target.value;
                            setProfile({ ...profile, faqs: newFaqs });
                          }}
                          multiline
                          rows={2}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Answer"
                          value={faq.answer}
                          onChange={(e) => {
                            const newFaqs = [...profile.faqs];
                            newFaqs[index].answer = e.target.value;
                            setProfile({ ...profile, faqs: newFaqs });
                          }}
                          multiline
                          rows={2}
                        />
                      </Grid>
                      <Grid item xs={12} md={1}>
                        <IconButton
                          color="error"
                          onClick={() => {
                            const newFaqs = profile.faqs.filter((_, i) => i !== index);
                            setProfile({ ...profile, faqs: newFaqs });
                          }}
                        >
                          <Trash size={20} />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}

                <Button
                  variant="outlined"
                  startIcon={<Plus />}
                  onClick={() => {
                    const newFaqs = [...(profile.faqs || []), { question: '', answer: '' }];
                    setProfile({
                      ...profile,
                      faqs: newFaqs
                    });
                  }}
                  sx={{ mt: 2 }}
                >
                  Add FAQ
                </Button>
              </Box>
              </TabPanel>

              {/* Recommended Suppliers Tab */}
              <TabPanel value={activeTab} sectionId="teamAndRecommendations">
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    Recommended Suppliers
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Recommend other suppliers to couples and share your supplier ID
                  </Typography>
                </Box>

                {/* Your Supplier ID Section */}
                {selectedBusinessId && (
                  <Card sx={{ mb: 4 }}>
                    <CardContent>
                      <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Your Supplier ID
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            bgcolor: 'grey.100', 
                            px: 1, 
                            py: 0.5, 
                            borderRadius: 1,
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {selectedBusinessId}
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Copy size={16} />}
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(selectedBusinessId);
                              setSuccess('Supplier ID copied to clipboard!');
                              setTimeout(() => setSuccess(''), 3000);
                            } catch (err) {
                              setError('Failed to copy Supplier ID');
                            }
                          }}
                          sx={{ minWidth: 'auto', px: 1.5 }}
                        >
                          Copy
                        </Button>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Share your supplier ID with your recommended suppliers
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* Recommended Suppliers Section */}
                <Box>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'stretch', sm: 'center' },
                    mb: 2,
                    gap: { xs: 2, sm: 0 }
                  }}>
                    <Typography variant="h6" component="h3">
                      Recommended Suppliers
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Plus size={20} />}
                      onClick={() => {
                        setRecommendedSupplierId('');
                        setRecommendedSupplierDialogError('');
                        setRecommendedSupplierDialogOpen(true);
                      }}
                      sx={{ 
                        width: { xs: '100%', sm: 'auto' }
                      }}
                    >
                      Add Recommended Supplier
                    </Button>
                  </Box>

                  {profile?.recommendedSuppliers && profile.recommendedSuppliers.length > 0 ? (
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                      {profile.recommendedSuppliers.map((supplierId) => {
                        // Handle both string IDs and ObjectId objects
                        const id = typeof supplierId === 'string' ? supplierId : (supplierId._id || supplierId.toString());
                        return (
                          <RecommendedSupplierCard
                            key={id}
                            supplierId={id}
                            ownerBusinessId={selectedBusinessId}
                            onRemove={async () => {
                              try {
                                await api.delete(`/api/supplier/recommended-suppliers/${id}`, {
                                  params: selectedBusinessId ? { businessId: String(selectedBusinessId) } : undefined
                                });
                                await fetchSupplierProfile();
                              } catch (error) {
                                setError('Failed to remove recommended supplier');
                              }
                            }}
                          />
                        );
                      })}
                    </Grid>
                  ) : (
                    <Alert severity="info">You have not added any recommendations yet. Add suppliers you work with and trust using the button below.</Alert>
                  )}
                </Box>

                {/* Recommended Supplier Dialog */}
                <Dialog open={recommendedSupplierDialogOpen} onClose={() => { setRecommendedSupplierDialogOpen(false); setRecommendedSupplierDialogError(''); }} maxWidth="sm" fullWidth>
                  <DialogTitle>Add Recommended Supplier</DialogTitle>
                  <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      {recommendedSupplierDialogError && (
                        <Alert severity="error" onClose={() => setRecommendedSupplierDialogError('')}>
                          {recommendedSupplierDialogError}
                        </Alert>
                      )}
                      <TextField
                        label="Business ID"
                        required
                        fullWidth
                        value={recommendedSupplierId}
                        onChange={(e) => setRecommendedSupplierId(e.target.value)}
                        helperText="Enter the business ID of the supplier you want to recommend"
                      />
                    </Stack>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setRecommendedSupplierDialogOpen(false)}>Cancel</Button>
                    <Button
                      variant="contained"
                      onClick={async () => {
                        if (!recommendedSupplierId.trim()) {
                          setRecommendedSupplierDialogError('Business ID is required');
                          return;
                        }

                        try {
                          setRecommendedSuppliersLoading(true);
                          setRecommendedSupplierDialogError('');
                          await api.post('/api/supplier/recommended-suppliers', {
                            businessId: recommendedSupplierId.trim(),
                            ...(selectedBusinessId ? { ownerBusinessId: String(selectedBusinessId) } : {})
                          }, {
                            params: selectedBusinessId ? { businessId: String(selectedBusinessId) } : undefined
                          });
                          await fetchSupplierProfile();
                          setRecommendedSupplierDialogOpen(false);
                          setRecommendedSupplierId('');
                        } catch (error) {
                          const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to add recommended supplier. Please check the Business ID and try again.';
                          setRecommendedSupplierDialogError(msg);
                        } finally {
                          setRecommendedSuppliersLoading(false);
                        }
                      }}
                      disabled={!recommendedSupplierId.trim() || recommendedSuppliersLoading}
                    >
                      {recommendedSuppliersLoading ? 'Adding...' : 'Add'}
                    </Button>
                  </DialogActions>
                </Dialog>
              </TabPanel>

              {/* Files Tab */}
              <TabPanel value={activeTab} sectionId="brochure">
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
                    {sections.find(s => s.id === 'brochure')?.label || 'My Files'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {sections.find(s => s.id === 'brochure')?.description || 'Upload files such as menus, brochures, packages, and more. Toggle visibility to allow couples to download.'}
                  </Typography>
                </Box>
                <MyFilesSection 
                  profile={profile} 
                  setProfile={setProfile} 
                  onUpdate={fetchSupplierProfile}
                  businessId={selectedBusinessId}
                />
              </TabPanel>
            </CardContent>
            
            {/* Sticky Action Buttons - Scrolls with page, sticks to bottom when visible */}
            <Box
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 10,
                mt: 'auto',
                backgroundColor: 'unset',
                background: 'unset',
                backgroundImage: 'none',
                backdropFilter: 'blur(20px)',
                pt: 2
              }}
            >
              <Card sx={{ m: 2, backgroundColor: 'background.paper', boxShadow: theme.shadows[4] }}>
                <CardContent sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'flex-end', 
                  gap: 2, 
                  p: { xs: 1.5, sm: 2 },
                  backgroundColor: 'unset',
                  background: 'unset'
                }}>
                  {selectedBusinessId && (
                    <Tooltip 
                      title={
                        profile?.slug 
                          ? "View your public profile page" 
                          : "Save your profile first to generate a public URL"
                      }
                    >
                      <span style={{ width: '100%', display: 'block' }}>
                        <Button
                          variant="outlined"
                          color="secondary"
                          startIcon={<ArrowSquareOut size={20} />}
                          onClick={() => {
                            if (profile?.slug) {
                              const profileUrl = `/profile/${selectedBusinessId}/${profile.slug}`;
                              window.open(profileUrl, '_blank', 'noopener,noreferrer');
                            } else {
                              // Show message that they need to save first
                              setError('Please save your profile first to generate a public URL. The slug will be created automatically when you save.');
                            }
                          }}
                          disabled={!profile?.slug}
                          fullWidth
                          sx={{ 
                            width: { xs: '100%', sm: 'auto' }
                          }}
                        >
                          View Profile Page
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving ? <CircularProgress size={20} /> : <Check />}
                    sx={{ 
                      py: 1.5, 
                      px: 4,
                      width: { xs: '100%', sm: 'auto' },
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {saving ? 'Saving...' : 'Save Profile'}
                  </Button>
                </CardContent>
              </Card>
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

// Helper function to get category label from value
function getCategoryLabel(categoryValue) {
  return supplierCategoryLabels[categoryValue] || categoryValue;
}

// Category-specific components
function VenueFields({ profile, setProfile }) {
  const handleChange = (field) => (event) => {
    setProfile({
      ...profile,
      venueDetails: {
        ...profile.venueDetails,
        [field]: event.target.value
      }
    });
  };

  const handleCapacityChange = (field) => (event) => {
    const value = parseInt(event.target.value, 10);
    setProfile({
      ...profile,
      venueDetails: {
        ...profile.venueDetails,
        [field]: isNaN(value) ? '' : value
      }
    });
  };

  const handleCapacityToggle = (field) => (event) => {
    setProfile({
      ...profile,
      venueDetails: {
        ...profile.venueDetails,
        [field]: event.target.checked
      }
    });
  };

  const handleFeatureToggle = (feature) => (event) => {
    const currentFeatures = profile.venueDetails?.features || [];
    const newFeatures = event.target.checked
      ? [...currentFeatures, feature]
      : currentFeatures.filter(f => f !== feature);
    
    setProfile({
      ...profile,
      venueDetails: {
        ...profile.venueDetails,
        features: newFeatures
      }
    });
  };

  const handleArrayToggle = (field, value) => {
    const currentArray = profile.venueDetails?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setProfile({
      ...profile,
      venueDetails: {
        ...profile.venueDetails,
        [field]: newArray
      }
    });
  };

  const venueTypes = supplierStyleOptions.venue?.venueType || [];

  const styleSpecializations = supplierStyleOptions.venue?.styleSpecialization || [];
  const capacitySpecializations = supplierStyleOptions.venue?.capacitySpecialization || [];

  const features = [
    'Parking', 'Catering', 'Bar Service', 'Dance Floor', 'Sound System', 'Lighting', 'Outdoor Space', 'Bridal Suite', 'Groom Suite', 'Late License', 'Dry Hire'
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Venue Type
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {venueTypes.map((type) => (
            <Chip
              key={type}
              label={type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
              onClick={() => handleArrayToggle('venueType', type)}
              color={(profile.venueDetails?.venueType || []).includes(type) ? 'primary' : 'default'}
              variant={(profile.venueDetails?.venueType || []).includes(type) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Style Specialization (Decor & Styling)
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {styleSpecializations.map((style) => (
            <Chip
              key={style}
              label={style.charAt(0).toUpperCase() + style.slice(1).replace('_', ' ')}
              onClick={() => handleArrayToggle('styleSpecialization', style)}
              color={(profile.venueDetails?.styleSpecialization || []).includes(style) ? 'primary' : 'default'}
              variant={(profile.venueDetails?.styleSpecialization || []).includes(style) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Capacity Specialization
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {capacitySpecializations.map((size) => (
            <Chip
              key={size}
              label={size}
              onClick={() => handleArrayToggle('capacitySpecialization', size)}
              color={(profile.venueDetails?.capacitySpecialization || []).includes(size) ? 'primary' : 'default'}
              variant={(profile.venueDetails?.capacitySpecialization || []).includes(size) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Capacities
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <FormControlLabel
          sx={{
            pl: { xs: 1, sm: 0 },
            '& .MuiSwitch-root': { mr: 1 }
          }}
          control={
            <Switch
              checked={profile.venueDetails?.canHostCeremony || false}
              onChange={handleCapacityToggle('canHostCeremony')}
              color="primary"
            />
          }
          label="Can host ceremonies?"
        />
        {profile.venueDetails?.canHostCeremony && (
          <Box sx={{ mt: 1, ml: { xs: 1, sm: 4 }, maxWidth: { xs: '100%', sm: 280 } }}>
            <TextField
              fullWidth
              sx={{ mt: 1 }}
              label="Ceremony capacity"
              type="number"
              value={profile.venueDetails?.ceremonyCapacity ?? ''}
              onChange={handleCapacityChange('ceremonyCapacity')}
              helperText="Maximum number of guests for ceremony"
              inputProps={{ min: 0 }}
            />
          </Box>
        )}
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <FormControlLabel
          sx={{
            pl: { xs: 1, sm: 0 },
            '& .MuiSwitch-root': { mr: 1 }
          }}
          control={
            <Switch
              checked={profile.venueDetails?.canHostReception || false}
              onChange={handleCapacityToggle('canHostReception')}
              color="primary"
            />
          }
          label="Can host receptions?"
        />
        {profile.venueDetails?.canHostReception && (
          <Box sx={{ mt: 1, ml: { xs: 1, sm: 4 }, maxWidth: { xs: '100%', sm: 280 } }}>
            <TextField
              fullWidth
              sx={{ mt: 1 }}
              label="Reception capacity"
              type="number"
              value={profile.venueDetails?.receptionCapacity ?? ''}
              onChange={handleCapacityChange('receptionCapacity')}
              helperText="Maximum number of guests for reception"
              inputProps={{ min: 0 }}
            />
          </Box>
        )}
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <FormControlLabel
          sx={{
            pl: { xs: 1, sm: 0 },
            '& .MuiSwitch-root': { mr: 1 }
          }}
          control={
            <Switch
              checked={profile.venueDetails?.canHostEvening || false}
              onChange={handleCapacityToggle('canHostEvening')}
              color="primary"
            />
          }
          label="Can host evening celebrations?"
        />
        {profile.venueDetails?.canHostEvening && (
          <Box sx={{ mt: 1, ml: { xs: 1, sm: 4 }, maxWidth: { xs: '100%', sm: 280 } }}>
            <TextField
              fullWidth
              sx={{ mt: 1 }}
              label="Evening capacity"
              type="number"
              value={profile.venueDetails?.eveningCapacity ?? ''}
              onChange={handleCapacityChange('eveningCapacity')}
              helperText="Maximum number of guests for evening"
              inputProps={{ min: 0 }}
            />
          </Box>
        )}
      </Grid>

      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Location Description"
          value={profile.venueDetails?.locationDescription || ''}
          onChange={handleChange('locationDescription')}
          multiline
          rows={2}
          helperText="Describe the location and surroundings"
        />
      </Grid>
      
      <Grid item xs={12}>
        <Typography variant="subtitle1" gutterBottom>
          Features
        </Typography>
        <Grid container spacing={1}>
          {features.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature}>
              <FormControlLabel
                control={
                  <Switch
                    checked={profile.venueDetails?.features?.includes(feature.toLowerCase()) || false}
                    onChange={handleFeatureToggle(feature.toLowerCase())}
                    color="primary"
                  />
                }
                label={feature}
              />
            </Grid>
          ))}
        </Grid>
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Special Requirements"
          value={profile.venueDetails?.specialRequirements || ''}
          onChange={handleChange('specialRequirements')}
          multiline
          rows={3}
          helperText="Any special requirements or restrictions"
        />
      </Grid>
    </Grid>
  );
}

function PhotographerFields({ profile, setProfile }) {
  const handleChange = (field) => (event) => {
    setProfile({
      ...profile,
      photographyDetails: {
        ...profile.photographyDetails,
        [field]: event.target.value
      }
    });
  };

  const handleArrayToggle = (field, value) => {
    const currentArray = profile.photographyDetails?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setProfile({
      ...profile,
      photographyDetails: {
        ...profile.photographyDetails,
        [field]: newArray
      }
    });
  };

  const handleBooleanToggle = (field) => {
    setProfile({
      ...profile,
      photographyDetails: {
        ...profile.photographyDetails,
        [field]: !profile.photographyDetails?.[field]
      }
    });
  };

  const styles = supplierStyleOptions.photographer?.style || [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Photography Style
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {styles.map((style) => (
            <Chip
              key={style}
              label={style.charAt(0).toUpperCase() + style.slice(1)}
              onClick={() => handleArrayToggle('style', style)}
              color={(profile.photographyDetails?.style || []).includes(style) ? 'primary' : 'default'}
              variant={(profile.photographyDetails?.style || []).includes(style) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Features
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={profile.photographyDetails?.secondShooterAvailable || false}
                onChange={() => handleBooleanToggle('secondShooterAvailable')}
                color="primary"
              />
            }
            label="Second Shooter Available"
          />
          <FormControlLabel
            control={
              <Switch
                checked={profile.photographyDetails?.droneAvailable || false}
                onChange={() => handleBooleanToggle('droneAvailable')}
                color="primary"
              />
            }
            label="Drone Available"
          />
        </FormGroup>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Experience"
          value={profile.photographyDetails?.experience || ''}
          onChange={handleChange('experience')}
          helperText="Years of experience"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Equipment"
          value={profile.photographyDetails?.equipment || ''}
          onChange={handleChange('equipment')}
          helperText="Camera equipment and gear"
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Services Included"
          value={profile.photographyDetails?.servicesIncluded || ''}
          onChange={handleChange('servicesIncluded')}
          multiline
          rows={3}
          helperText="What's included in your packages"
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Delivery Time"
          value={profile.photographyDetails?.deliveryTime || ''}
          onChange={handleChange('deliveryTime')}
          helperText="How long until photos are delivered"
        />
      </Grid>
    </Grid>
  );
}

function VideographerFields({ profile, setProfile }) {
  const handleArrayToggle = (field, value) => {
    const currentArray = profile.videographyDetails?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setProfile({
      ...profile,
      videographyDetails: {
        ...profile.videographyDetails,
        [field]: newArray
      }
    });
  };

  const handleBooleanToggle = (field) => {
    setProfile({
      ...profile,
      videographyDetails: {
        ...profile.videographyDetails,
        [field]: !profile.videographyDetails?.[field]
      }
    });
  };

  const styles = supplierStyleOptions.videographer?.style || [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Videography Style
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {styles.map((style) => (
            <Chip
              key={style}
              label={style.charAt(0).toUpperCase() + style.slice(1)}
              onClick={() => handleArrayToggle('style', style)}
              color={(profile.videographyDetails?.style || []).includes(style) ? 'primary' : 'default'}
              variant={(profile.videographyDetails?.style || []).includes(style) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Features
        </Typography>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={profile.videographyDetails?.secondShooterAvailable || false}
                onChange={() => handleBooleanToggle('secondShooterAvailable')}
                color="primary"
              />
            }
            label="Second Shooter Available"
          />
          <FormControlLabel
            control={
              <Switch
                checked={profile.videographyDetails?.droneAvailable || false}
                onChange={() => handleBooleanToggle('droneAvailable')}
                color="primary"
              />
            }
            label="Drone Available"
          />
        </FormGroup>
      </Grid>
    </Grid>
  );
}

function DecorFields({ profile, setProfile }) {
  const handleArrayToggle = (field, value) => {
    const currentArray = profile.decorDetails?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setProfile({
      ...profile,
      decorDetails: {
        ...profile.decorDetails,
        [field]: newArray
      }
    });
  };

  const styleSpecializations = supplierStyleOptions.decor?.styleSpecialization || [];

  const services = supplierStyleOptions.decor?.services || [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Style Specialization
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {styleSpecializations.map((style) => (
            <Chip
              key={style}
              label={style.charAt(0).toUpperCase() + style.slice(1).replace('_', ' ')}
              onClick={() => handleArrayToggle('styleSpecialization', style)}
              color={(profile.decorDetails?.styleSpecialization || []).includes(style) ? 'primary' : 'default'}
              variant={(profile.decorDetails?.styleSpecialization || []).includes(style) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Services Offered
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {services.map((service) => (
            <Chip
              key={service}
              label={service.charAt(0).toUpperCase() + service.slice(1).replace(/_/g, ' ')}
              onClick={() => handleArrayToggle('services', service)}
              color={(profile.decorDetails?.services || []).includes(service) ? 'primary' : 'default'}
              variant={(profile.decorDetails?.services || []).includes(service) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>
    </Grid>
  );
}

function CatererFields({ profile, setProfile }) {
  const handleArrayToggle = (field, value) => {
    const currentArray = profile.foodDetails?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setProfile({
      ...profile,
      foodDetails: {
        ...profile.foodDetails,
        [field]: newArray
      }
    });
  };

  const serviceStyles = supplierStyleOptions.caterer?.serviceStyle || [];

  const dietaryOptions = supplierStyleOptions.caterer?.dietaryOptions || [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Service Style
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {serviceStyles.map((style) => (
            <Chip
              key={style}
              label={style.charAt(0).toUpperCase() + style.slice(1).replace(/_/g, ' ')}
              onClick={() => handleArrayToggle('serviceStyle', style)}
              color={(profile.foodDetails?.serviceStyle || []).includes(style) ? 'primary' : 'default'}
              variant={(profile.foodDetails?.serviceStyle || []).includes(style) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Dietary Options
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {dietaryOptions.map((option) => (
            <Chip
              key={option}
              label={option.charAt(0).toUpperCase() + option.slice(1).replace(/_/g, ' ')}
              onClick={() => handleArrayToggle('dietaryOptions', option)}
              color={(profile.foodDetails?.dietaryOptions || []).includes(option) ? 'primary' : 'default'}
              variant={(profile.foodDetails?.dietaryOptions || []).includes(option) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>
    </Grid>
  );
}

function FloristFields({ profile, setProfile }) {
  const handleArrayToggle = (field, value) => {
    const currentArray = profile.floristDetails?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setProfile({
      ...profile,
      floristDetails: {
        ...profile.floristDetails,
        [field]: newArray
      }
    });
  };

  const styles = supplierStyleOptions.florist?.styles || [];

  const services = supplierStyleOptions.florist?.services || [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Floral Styles
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {styles.map((style) => (
            <Chip
              key={style}
              label={style.charAt(0).toUpperCase() + style.slice(1).replace('_', ' ')}
              onClick={() => handleArrayToggle('styles', style)}
              color={(profile.floristDetails?.styles || []).includes(style) ? 'primary' : 'default'}
              variant={(profile.floristDetails?.styles || []).includes(style) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Services Offered
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {services.map((service) => (
            <Chip
              key={service}
              label={service.charAt(0).toUpperCase() + service.slice(1).replace(/_/g, ' ')}
              onClick={() => handleArrayToggle('services', service)}
              color={(profile.floristDetails?.services || []).includes(service) ? 'primary' : 'default'}
              variant={(profile.floristDetails?.services || []).includes(service) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>
    </Grid>
  );
}

function CakeMakerFields({ profile, setProfile }) {
  const handleChange = (field) => (event) => {
    setProfile({
      ...profile,
      cakeDetails: {
        ...profile.cakeDetails,
        [field]: event.target.value
      }
    });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Cake Styles"
          value={profile.cakeDetails?.cakeStyles || ''}
          onChange={handleChange('cakeStyles')}
          multiline
          rows={3}
          helperText="Describe your cake design styles"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Flavors"
          value={profile.cakeDetails?.flavors || ''}
          onChange={handleChange('flavors')}
          helperText="Available cake flavors"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Serving Size"
          value={profile.cakeDetails?.servingSize || ''}
          onChange={handleChange('servingSize')}
          helperText="Number of servings"
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Special Dietary Options"
          value={profile.cakeDetails?.specialDietary || ''}
          onChange={handleChange('specialDietary')}
          multiline
          rows={2}
          helperText="Gluten-free, vegan, etc."
        />
      </Grid>
    </Grid>
  );
}

function DressDesignerFields({ profile, setProfile }) {
  const handleChange = (field) => (event) => {
    setProfile({
      ...profile,
      dressDetails: {
        ...profile.dressDetails,
        [field]: event.target.value
      }
    });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Design Style"
          value={profile.dressDetails?.designStyle || ''}
          onChange={handleChange('designStyle')}
          multiline
          rows={3}
          helperText="Describe your dress design style"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Timeline"
          value={profile.dressDetails?.timeline || ''}
          onChange={handleChange('timeline')}
          helperText="How long to create a dress"
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Services Included"
          value={profile.dressDetails?.servicesIncluded || ''}
          onChange={handleChange('servicesIncluded')}
          multiline
          rows={2}
          helperText="Fittings, alterations, etc."
        />
      </Grid>
    </Grid>
  );
}

function PlannerFields({ profile, setProfile }) {
  const handleArrayToggle = (field, value) => {
    const currentArray = profile.plannerDetails?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setProfile({
      ...profile,
      plannerDetails: {
        ...profile.plannerDetails,
        [field]: newArray
      }
    });
  };

  const services = supplierStyleOptions.planner?.services || [];

  const specializations = supplierStyleOptions.planner?.specialization || [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Services Offered
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {services.map((service) => (
            <Chip
              key={service}
              label={service.charAt(0).toUpperCase() + service.slice(1).replace(/_/g, ' ')}
              onClick={() => handleArrayToggle('services', service)}
              color={(profile.plannerDetails?.services || []).includes(service) ? 'primary' : 'default'}
              variant={(profile.plannerDetails?.services || []).includes(service) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Specialization
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {specializations.map((spec) => (
            <Chip
              key={spec}
              label={spec.charAt(0).toUpperCase() + spec.slice(1).replace(/_/g, ' ')}
              onClick={() => handleArrayToggle('specialization', spec)}
              color={(profile.plannerDetails?.specialization || []).includes(spec) ? 'primary' : 'default'}
              variant={(profile.plannerDetails?.specialization || []).includes(spec) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>
    </Grid>
  );
}

function EntertainmentFields({ profile, setProfile }) {
  const [genreInput, setGenreInput] = useState('');

  const handlePerformanceTypeToggle = (type) => {
    const currentArray = Array.isArray(profile.entertainmentDetails?.performanceType) 
      ? profile.entertainmentDetails.performanceType 
      : (profile.entertainmentDetails?.performanceType ? [profile.entertainmentDetails.performanceType] : []);
    
    const newArray = currentArray.includes(type)
      ? currentArray.filter(item => item !== type)
      : [...currentArray, type];
    
    setProfile({
      ...profile,
      entertainmentDetails: {
        ...profile.entertainmentDetails,
        performanceType: newArray
      }
    });
  };

  const handleAddGenre = () => {
    if (genreInput.trim() && !(profile.entertainmentDetails?.genres || []).includes(genreInput.trim())) {
      setProfile({
        ...profile,
        entertainmentDetails: {
          ...profile.entertainmentDetails,
          genres: [...(profile.entertainmentDetails?.genres || []), genreInput.trim()]
        }
      });
      setGenreInput('');
    }
  };

  const handleRemoveGenre = (genre) => {
    setProfile({
      ...profile,
      entertainmentDetails: {
        ...profile.entertainmentDetails,
        genres: (profile.entertainmentDetails?.genres || []).filter(g => g !== genre)
      }
    });
  };

  const performanceTypes = supplierStyleOptions[profile.category]?.performanceType ||
    supplierStyleOptions.music_live_entertainment?.performanceType || [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Performance Type
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {performanceTypes.map((type) => {
            const currentArray = Array.isArray(profile.entertainmentDetails?.performanceType) 
              ? profile.entertainmentDetails.performanceType 
              : (profile.entertainmentDetails?.performanceType ? [profile.entertainmentDetails.performanceType] : []);
            const isSelected = currentArray.includes(type);
            
            return (
              <Chip
                key={type}
                label={type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ')}
                onClick={() => handlePerformanceTypeToggle(type)}
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
              />
            );
          })}
        </Box>
      </Grid>

      {['band', 'dj', 'music_live_entertainment'].includes(profile.category) && (
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Music Genres
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Add genre (e.g., Jazz, Rock, Pop)"
              value={genreInput}
              onChange={(e) => setGenreInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddGenre();
                }
              }}
            />
            <Button variant="outlined" onClick={handleAddGenre} disabled={!genreInput.trim()}>
              Add
            </Button>
          </Box>
          {(profile.entertainmentDetails?.genres || []).length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {profile.entertainmentDetails.genres.map((genre) => (
                <Chip
                  key={genre}
                  label={genre}
                  onDelete={() => handleRemoveGenre(genre)}
                  color="primary"
                />
              ))}
            </Box>
          )}
        </Grid>
      )}
    </Grid>
  );
}

function BeautyFields({ profile, setProfile }) {
  const handleArrayToggle = (field, value) => {
    const currentArray = profile.beautyDetails?.[field] || [];
    const newArray = currentArray.includes(value)
      ? currentArray.filter(item => item !== value)
      : [...currentArray, value];
    
    setProfile({
      ...profile,
      beautyDetails: {
        ...profile.beautyDetails,
        [field]: newArray
      }
    });
  };

  const handleBooleanToggle = (field) => {
    setProfile({
      ...profile,
      beautyDetails: {
        ...profile.beautyDetails,
        [field]: !profile.beautyDetails?.[field]
      }
    });
  };

  const styleSpecializations = supplierStyleOptions.hair?.styleSpecialization || [];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Style Specialization
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {styleSpecializations.map((style) => (
            <Chip
              key={style}
              label={style.charAt(0).toUpperCase() + style.slice(1).replace('_', ' ')}
              onClick={() => handleArrayToggle('styleSpecialization', style)}
              color={(profile.beautyDetails?.styleSpecialization || []).includes(style) ? 'primary' : 'default'}
              variant={(profile.beautyDetails?.styleSpecialization || []).includes(style) ? 'filled' : 'outlined'}
            />
          ))}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Switch
              checked={profile.beautyDetails?.onSiteService || false}
              onChange={() => handleBooleanToggle('onSiteService')}
              color="primary"
            />
          }
          label="On-Site Service Available"
        />
      </Grid>

    </Grid>
  );
}

function OtherFields({ profile, setProfile }) {
  const handleChange = (field) => (event) => {
    setProfile({
      ...profile,
      otherDetails: {
        ...profile.otherDetails,
        [field]: event.target.value
      }
    });
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Service Type"
          value={profile.otherDetails?.serviceType || ''}
          onChange={handleChange('serviceType')}
          helperText="What type of service do you provide?"
        />
      </Grid>
      
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Service Description"
          value={profile.otherDetails?.serviceDescription || ''}
          onChange={handleChange('serviceDescription')}
          multiline
          rows={4}
          helperText="Describe your services in detail"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Experience"
          value={profile.otherDetails?.experience || ''}
          onChange={handleChange('experience')}
          helperText="Years of experience"
        />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Specialties"
          value={profile.otherDetails?.specialties || ''}
          onChange={handleChange('specialties')}
          helperText="What makes you unique?"
        />
      </Grid>
    </Grid>
  );
}

export default SupplierProfileEdit;
